import {
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { CvFieldConfig, CvReviewSection } from './cv-import-review.model';
import {
  CvDraftEntry,
  CvProfileDraft,
} from '../../shared/model/cv-import.model';
import moment from 'moment';

import { joinList, normalizeDate, splitList } from './cv-import.util';

const PERSONALDATEN_FIELDS: CvFieldConfig[] = [
  {
    key: 'intro',
    labelKey: 'cvImport.fields.intro',
    type: 'textarea',
    rows: 6,
  },
  { key: 'position', labelKey: 'cvImport.fields.position', type: 'text' },
  { key: 'wohnort', labelKey: 'cvImport.fields.wohnort', type: 'text' },
  {
    key: 'nationalitaet',
    labelKey: 'cvImport.fields.nationalitaet',
    type: 'text',
  },
  { key: 'jahrgang', labelKey: 'cvImport.fields.jahrgang', type: 'birthdate' },
  { key: 'beraterSeit', labelKey: 'cvImport.fields.beraterSeit', type: 'text' },
  { key: 'verfuegbar', labelKey: 'cvImport.fields.verfuegbar', type: 'date' },
  {
    key: 'reisebereitschaft',
    labelKey: 'cvImport.fields.reisebereitschaft',
    type: 'text',
  },
  {
    key: 'einschraenkung',
    labelKey: 'cvImport.fields.einschraenkung',
    type: 'text',
  },
  { key: 'sprachen', labelKey: 'cvImport.fields.sprachen', type: 'sprachen' },
  {
    key: 'branchen',
    labelKey: 'cvImport.fields.branchen',
    type: 'list',
    maxItems: 7,
  },
  {
    key: 'schwerpunkte',
    labelKey: 'cvImport.fields.schwerpunkte',
    type: 'list',
    maxItems: 5,
  },
];

const WERDEGANG_FIELDS: CvFieldConfig[] = [
  {
    key: 'unternehmen',
    labelKey: 'cvImport.fields.unternehmen',
    type: 'text',
  },
  {
    key: 'stellenbezeichnung',
    labelKey: 'cvImport.fields.stellenbezeichnung',
    type: 'text',
  },
  {
    key: 'taetigkeit',
    labelKey: 'cvImport.fields.taetigkeit',
    type: 'textarea',
    rows: 3,
  },
  { key: 'ort', labelKey: 'cvImport.fields.ort', type: 'text' },
  { key: 'beginn', labelKey: 'cvImport.fields.beginn', type: 'date' },
  { key: 'ende', labelKey: 'cvImport.fields.ende', type: 'date' },
];

const AUSBILDUNG_FIELDS: CvFieldConfig[] = [
  {
    key: 'ausbildungseinrichtung',
    labelKey: 'cvImport.fields.ausbildungseinrichtung',
    type: 'text',
  },
  {
    key: 'ausbildungsgang',
    labelKey: 'cvImport.fields.ausbildungsgang',
    type: 'text',
  },
  { key: 'abschluss', labelKey: 'cvImport.fields.abschluss', type: 'text' },
  { key: 'ort', labelKey: 'cvImport.fields.ort', type: 'text' },
  { key: 'beginn', labelKey: 'cvImport.fields.beginn', type: 'date' },
  { key: 'ende', labelKey: 'cvImport.fields.ende', type: 'date' },
  {
    key: 'abschlussErreicht',
    labelKey: 'cvImport.fields.abschlussErreicht',
    type: 'checkbox',
  },
];

const PROJEKTE_FIELDS: CvFieldConfig[] = [
  {
    key: 'projektname',
    labelKey: 'cvImport.fields.projektname',
    type: 'text',
  },
  {
    key: 'kunde',
    labelKey: 'cvImport.fields.kunde',
    type: 'text',
  },
  {
    key: 'stelle',
    labelKey: 'cvImport.fields.stelle',
    type: 'text',
  },
  {
    key: 'beginn',
    labelKey: 'cvImport.fields.beginn',
    type: 'date',
  },
  { key: 'ende', labelKey: 'cvImport.fields.ende', type: 'date' },
  {
    key: 'beschreibung',
    labelKey: 'cvImport.fields.beschreibung',
    type: 'textarea',
    rows: 8,
    maxLength: 900,
  },
  {
    key: 'teamSize',
    labelKey: 'cvImport.fields.teamSize',
    type: 'number',
    min: 1,
  },
  {
    key: 'numberTester',
    labelKey: 'cvImport.fields.numberTester',
    type: 'number',
    min: 0,
  },
  {
    key: 'projectSize',
    labelKey: 'cvImport.fields.projectSize',
    type: 'number',
    min: 1,
  },
  { key: 'methodik', labelKey: 'cvImport.fields.methodik', type: 'list' },
  {
    key: 'workLocation',
    labelKey: 'cvImport.fields.workLocation',
    type: 'text',
    maxLength: 100,
  },
  {
    key: 'branchNames',
    labelKey: 'cvImport.fields.branchNames',
    type: 'industries',
  },
  { key: 'tasks', labelKey: 'cvImport.fields.tasks', type: 'tasks' },
];

const ZERTIFIKATE_FIELDS: CvFieldConfig[] = [
  {
    key: 'lehrgang',
    labelKey: 'cvImport.fields.lehrgang',
    type: 'text',
  },
  {
    key: 'schulungsanbieter',
    labelKey: 'cvImport.fields.schulungsanbieter',
    type: 'text',
  },
  {
    key: 'trainingsanbieter',
    labelKey: 'cvImport.fields.trainingsanbieter',
    type: 'text',
  },
  {
    key: 'zertifikatnummer',
    labelKey: 'cvImport.fields.zertifikatnummer',
    type: 'text',
  },
  { key: 'beginn', labelKey: 'cvImport.fields.beginn', type: 'date' },
];

const SCHULUNGEN_FIELDS: CvFieldConfig[] = [
  {
    key: 'lehrgang',
    labelKey: 'cvImport.fields.lehrgang',
    type: 'text',
  },
  {
    key: 'schulungsanbieter',
    labelKey: 'cvImport.fields.schulungsanbieter',
    type: 'text',
  },
  { key: 'beginn', labelKey: 'cvImport.fields.beginn', type: 'date' },
];

interface SectionDef {
  key: keyof CvProfileDraft;
  titleKey: string;
  single: boolean;
  fields: CvFieldConfig[];
}

/** Section order = wizard step order after upload. Faehigkeiten are deferred (NP20-758). */
const SECTION_DEFS: SectionDef[] = [
  {
    key: 'personaldaten',
    titleKey: 'cvImport.sections.personaldaten',
    single: true,
    fields: PERSONALDATEN_FIELDS,
  },
  {
    key: 'werdegang',
    titleKey: 'cvImport.sections.werdegang',
    single: false,
    fields: WERDEGANG_FIELDS,
  },
  {
    key: 'ausbildung',
    titleKey: 'cvImport.sections.ausbildung',
    single: false,
    fields: AUSBILDUNG_FIELDS,
  },
  {
    key: 'projekte',
    titleKey: 'cvImport.sections.projekte',
    single: false,
    fields: PROJEKTE_FIELDS,
  },
  {
    key: 'zertifikate',
    titleKey: 'cvImport.sections.zertifikate',
    single: false,
    fields: ZERTIFIKATE_FIELDS,
  },
  {
    key: 'schulungen',
    titleKey: 'cvImport.sections.schulungen',
    single: false,
    fields: SCHULUNGEN_FIELDS,
  },
];

function maxItemsValidator(max: number): ValidatorFn {
  return (control) => {
    const count = splitList(control.value as string).length;
    return count > max ? { maxItems: { max, actual: count } } : null;
  };
}

function fieldValidators(field: CvFieldConfig): ValidatorFn[] {
  const validators: ValidatorFn[] = [];
  if (field.required) {
    validators.push(Validators.required);
  }
  if (field.minLength != null) {
    validators.push(Validators.minLength(field.minLength));
  }
  if (field.maxLength != null) {
    validators.push(Validators.maxLength(field.maxLength));
  }
  if (field.min != null) {
    validators.push(Validators.min(field.min));
  }
  if (field.maxItems != null) {
    validators.push(maxItemsValidator(field.maxItems));
  }
  return validators;
}

const BIRTHDATE_FORMAT = 'DD.MM.YYYY';

const BIRTHDATE_INPUT_FORMATS = [
  BIRTHDATE_FORMAT,
  'D.M.YYYY',
  'D.MM.YYYY',
  'DD.M.YYYY',
  'YYYY-MM-DD',
  'YYYY',
];

/**
 * Reads a drafted jahrgang (DD.MM.YYYY or a bare YYYY, plus D.M.YYYY and ISO as a fallback).
 * Future dates are dropped so the prefill never violates the picker's max.
 */
function parseBirthdate(raw: unknown): moment.Moment | null {
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  const parsed = moment(raw.trim(), BIRTHDATE_INPUT_FORMATS, true);
  return parsed.isValid() && !parsed.isAfter(moment(), 'day') ? parsed : null;
}

function joinLanguages(raw: unknown): string {
  if (Array.isArray(raw)) {
    return raw
      .map((item) =>
        typeof item === 'string'
          ? item
          : typeof item === 'object' && item !== null && 'language' in item
            ? String((item as Record<string, unknown>)['language'])
            : '',
      )
      .filter((s) => s.length > 0)
      .join(', ');
  }
  return raw == null ? '' : String(raw);
}

function initialValue(
  field: CvFieldConfig,
  value: Record<string, unknown> | undefined,
): unknown {
  const raw = value?.[field.key];
  switch (field.type) {
    case 'sprachen':
      return joinLanguages(raw);
    case 'list':
      return joinList(raw);
    case 'date': {
      const iso = normalizeDate(raw);
      return iso ? moment(iso, 'YYYY-MM-DD') : null;
    }
    case 'birthdate':
      return parseBirthdate(raw);
    case 'number':
      return raw ?? null;
    case 'checkbox':
      return raw === true;
    case 'industries':
      return Array.isArray(raw)
        ? raw.map((item) => ({ ...(item as object) }))
        : [];
    case 'tasks':
      return Array.isArray(raw)
        ? raw.map((item) => {
            const task = item as Record<string, unknown>;
            return {
              caption: (task['caption'] as string) ?? '',
              details: (task['details'] as string) ?? '',
              skills: Array.isArray(task['skills'])
                ? (task['skills'] as object[]).map((skill) => ({ ...skill }))
                : [],
            };
          })
        : [];
    default:
      return raw ?? '';
  }
}

function buildFieldForm(
  fields: CvFieldConfig[],
  value: Record<string, unknown> | undefined,
): FormGroup {
  const controls: Record<string, FormControl> = {};
  for (const field of fields) {
    controls[field.key] = new FormControl(
      initialValue(field, value),
      fieldValidators(field),
    );
  }
  return new FormGroup(controls);
}

function hasAnyValue(
  fields: CvFieldConfig[],
  value: Record<string, unknown> | undefined,
): boolean {
  if (!value) {
    return false;
  }
  return fields.some((field) => {
    const raw = value[field.key];
    if (Array.isArray(raw)) {
      return raw.length > 0;
    }
    return raw != null && String(raw).trim().length > 0;
  });
}

/** Serializes an entry form back to a plain section object for the backend. */
export function serializeFields(
  fields: CvFieldConfig[],
  form: FormGroup,
): Record<string, unknown> {
  const raw = form.getRawValue();
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    const value = raw[field.key];
    switch (field.type) {
      case 'sprachen':
        out[field.key] = splitList(value as string);
        break;
      case 'list':
        out[field.key] = splitList(value);
        break;
      case 'date': {
        if (!value) {
          out[field.key] = null;
          break;
        }
        const parsed = moment(value);
        out[field.key] = parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
        break;
      }
      case 'birthdate': {
        const parsed = value ? moment(value) : null;
        out[field.key] = parsed?.isValid()
          ? parsed.format(BIRTHDATE_FORMAT)
          : null;
        break;
      }
      case 'number':
        out[field.key] = value === '' || value == null ? null : Number(value);
        break;
      case 'checkbox':
        out[field.key] = value === true;
        break;
      case 'industries':
        out[field.key] = Array.isArray(value) ? value : [];
        break;
      case 'tasks':
        out[field.key] = (Array.isArray(value) ? value : [])
          .map((item) => item as Record<string, unknown>)
          .filter(
            (task) =>
              String(task['caption'] ?? '').trim().length > 0 ||
              String(task['details'] ?? '').trim().length > 0 ||
              (Array.isArray(task['skills']) && task['skills'].length > 0),
          );
        break;
      default:
        out[field.key] = value === '' || value == null ? null : value;
    }
  }
  return out;
}

/**
 * True if a serialized entry carries at least one meaningful value. Nulls,
 * empty strings/arrays and unchecked booleans don't count — so a blank
 * composite entry (Werdegang/Ausbildung/Projekte/Zertifikate) isn't persisted.
 */
export function isEntryFilled(serialized: Record<string, unknown>): boolean {
  return Object.values(serialized).some((value) => {
    if (value == null) {
      return false;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  });
}

/** Builds the review sections from a draft, skipping sections with no proposals. */
export function buildReviewSections(draft: CvProfileDraft): CvReviewSection[] {
  const sections: CvReviewSection[] = [];

  for (const def of SECTION_DEFS) {
    if (def.single) {
      const entry = draft[def.key] as
        | CvDraftEntry<Record<string, unknown>>
        | undefined;
      if (entry && hasAnyValue(def.fields, entry.value)) {
        sections.push({
          ...def,
          entries: [
            {
              form: buildFieldForm(def.fields, entry.value),
              accepted: true,
              confidence: entry.confidence,
              source: entry.source,
            },
          ],
        });
      }
    } else {
      const list = draft[def.key] as
        | CvDraftEntry<Record<string, unknown>>[]
        | undefined;
      if (list && list.length > 0) {
        sections.push({
          ...def,
          entries: list.map((item) => ({
            form: buildFieldForm(def.fields, item.value ?? {}),
            accepted: true,
            confidence: item.confidence,
            source: item.source,
          })),
        });
      }
    }
  }

  return sections;
}
